import type { MaterialItemFragment } from "~/gql/queries/material.generated";

import { MaterialVendorData } from "~/gql/graphqlBase.generated";

export const EACH_UOM_LABEL = "each";

const MIN_PARTIAL_COUNT = 0.01;

export interface VendorUomInfo {
	uomLabel: string;
	unitsPerUom: number;
}

type VendorPack = Pick<MaterialVendorData, "purchaseUom" | "unitsPerUom">;

const EACH_UOM: VendorUomInfo = { uomLabel: EACH_UOM_LABEL, unitsPerUom: 1 };

export const hasCompleteVendorPackAssignment = <TAssignment extends VendorPack>(
	assignment: TAssignment | undefined
): assignment is TAssignment & { purchaseUom: string; unitsPerUom: number } =>
	Boolean(assignment?.purchaseUom) && (assignment?.unitsPerUom ?? 0) >= 1;

export const resolveVendorUom = (
	material: MaterialItemFragment | undefined,
	vendorId: string | undefined | null
): VendorUomInfo => {
	if (!material || !vendorId) return EACH_UOM;
	const assignment = material.vendors?.find((v) => v.vendorId === vendorId);
	if (!hasCompleteVendorPackAssignment(assignment)) return EACH_UOM;
	return { uomLabel: assignment.purchaseUom, unitsPerUom: assignment.unitsPerUom };
};

// Vendor cost stays per-each canonical on the wire; the grid shows/edits cost per the vendor's quoted pack and converts at the edge.
export const perEachToVendorUomCost = (perEachCost: number | null | undefined, unitsPerUom: number): number | null => {
	if (perEachCost === null || perEachCost === undefined || !Number.isFinite(perEachCost)) return null;
	return perEachCost * unitsPerUom;
};

export const vendorUomCostToPerEach = (
	vendorUomCost: number | null | undefined,
	unitsPerUom: number
): number | null => {
	if (vendorUomCost === null || vendorUomCost === undefined || !Number.isFinite(vendorUomCost)) return null;
	if (unitsPerUom < 1) return null;
	return vendorUomCost / unitsPerUom;
};

const PACK_QUANTITY_TOLERANCE = 0.00005;

const nearestPackCount = (quantityOrderedEaches: number, unitsPerUom: number): number =>
	Math.round(quantityOrderedEaches / unitsPerUom);

const isExactPackCount = (quantityOrderedEaches: number, unitsPerUom: number): boolean =>
	Math.abs(quantityOrderedEaches - nearestPackCount(quantityOrderedEaches, unitsPerUom) * unitsPerUom) <
	PACK_QUANTITY_TOLERANCE;

export const wholeVendorUomCount = (quantityOrderedEaches: number, unitsPerUom: number): number => {
	if (unitsPerUom < 1) return quantityOrderedEaches;
	return isExactPackCount(quantityOrderedEaches, unitsPerUom)
		? nearestPackCount(quantityOrderedEaches, unitsPerUom)
		: Math.ceil(quantityOrderedEaches / unitsPerUom);
};

export const isWholeVendorUomCount = (quantityOrderedEaches: number, unitsPerUom: number): boolean =>
	unitsPerUom < 1 || isExactPackCount(quantityOrderedEaches, unitsPerUom);

export const exactVendorUomCount = (quantityOrderedEaches: number, unitsPerUom: number): number => {
	const rounded = Math.round((quantityOrderedEaches / Math.max(unitsPerUom, 1)) * 100) / 100;
	if (isWholeVendorUomCount(quantityOrderedEaches, unitsPerUom)) return rounded;
	if (rounded <= 0) return MIN_PARTIAL_COUNT;
	const whole = wholeVendorUomCount(quantityOrderedEaches, unitsPerUom);
	return rounded >= whole ? Math.round((whole - 0.01) * 100) / 100 : rounded;
};
